import { useEffect, useState } from "react";

import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ServiceConstants } from "constant";
import { ContractAdjustmentService, ContractService } from "services";
import { Bank, ContractAdjustmentRequest, ContractAdjustmentResponse } from "types";

import { ContractDetails } from "./types";

const {
  REQUEST_CONTRACT_ADJUSTMENT_LIST_ID,
  REQUEST_CONTRACT_ADJUSTMENT_CREATE_ID,
  REQUEST_CONTRACT_ADJUSTMENT_APPROVE_ID,
  REQUEST_CONTRACT_ADJUSTMENT_CANCEL_ID,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
  REQUEST_ERROR,
} = ServiceConstants;

export const useContractAdjustments = (
  bankCreditRequestId: number,
  bank: Bank,
  contractId: number | undefined,
  contractDetails: ContractDetails | undefined
) => {
  const { t } = useTranslation();
  const [contractAdjustments, setContractAdjustments] = useState<ContractAdjustmentResponse[]>([]);
  const [anpassungOpen, setAnpassungOpen] = useState(false);
  const [anpassungCreditAmount, setAnpassungCreditAmount] = useState<number | undefined>(undefined);
  const [anpassungPaymentPeriod, setAnpassungPaymentPeriod] = useState<number | undefined>(undefined);
  const [anpassungMonthlyInstallment, setAnpassungMonthlyInstallment] = useState<number | undefined>(undefined);
  const [anpassungCreditInsurance, setAnpassungCreditInsurance] = useState("NO");
  const [anpassungComment, setAnpassungComment] = useState("");
  const [adjustmentActionTargetId, setAdjustmentActionTargetId] = useState<number | undefined>(undefined);
  const [adjustmentActionType, setAdjustmentActionType] = useState<"APPROVED" | "REJECTED" | undefined>(undefined);

  const contractAdjustmentsRequestKey = bankCreditRequestId
    ? REQUEST_CONTRACT_ADJUSTMENT_LIST_ID + bankCreditRequestId
    : undefined;

  const contractAdjustmentsResponse = useSelector(
    (state: any) =>
      contractAdjustmentsRequestKey ? state.service.object[contractAdjustmentsRequestKey] : undefined
  );
  const contractAdjustmentCreateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CONTRACT_ADJUSTMENT_CREATE_ID]
  );
  const serviceObjects = useSelector((state: any) => state.service.object);

  // Fetch adjustments when bank request context becomes available
  useEffect(() => {
    if (bankCreditRequestId && bank) {
      ContractAdjustmentService.getContractAdjustments(bankCreditRequestId, bank);
    }
  }, [bankCreditRequestId, bank]);

  // Handle adjustments list response
  useEffect(() => {
    if (!contractAdjustmentsResponse?.data || !Array.isArray(contractAdjustmentsResponse.data)) return;

    const sorted = [...(contractAdjustmentsResponse.data as ContractAdjustmentResponse[])].sort(
      (a, b) =>
        new Date(b.info?.createdAt || 0).getTime() - new Date(a.info?.createdAt || 0).getTime()
    );
    setContractAdjustments(sorted);
  }, [contractAdjustmentsResponse]);

  // Handle create response
  useEffect(() => {
    if (contractAdjustmentCreateResponse?.status === REQUEST_SUCCESS) {
      setAnpassungOpen(false);
      setAnpassungComment("");
      if (bankCreditRequestId && bank) {
        ContractAdjustmentService.getContractAdjustments(bankCreditRequestId, bank);
      }
    }

    if (contractAdjustmentCreateResponse?.status === REQUEST_ERROR) {
      notifications.show({
        title: t("bankSelection.anpassung.form.submit"),
        message: t("bankSelection.anpassung.form.error"),
        color: "red",
      });
    }
  }, [contractAdjustmentCreateResponse, bankCreditRequestId, bank, t]);

  // Handle approve/reject response
  useEffect(() => {
    if (!contractId || !adjustmentActionTargetId || !adjustmentActionType) return;

    const requestKey =
      adjustmentActionType === "APPROVED"
        ? REQUEST_CONTRACT_ADJUSTMENT_APPROVE_ID + adjustmentActionTargetId
        : REQUEST_CONTRACT_ADJUSTMENT_CANCEL_ID + adjustmentActionTargetId;

    const actionServiceObject = serviceObjects[requestKey];

    if (actionServiceObject?.status === REQUEST_SUCCESS) {
      ContractAdjustmentService.getContractAdjustments(bankCreditRequestId, bank);
      ContractService.getContract(bankCreditRequestId, bank);
      setAdjustmentActionTargetId(undefined);
      setAdjustmentActionType(undefined);
    }

    if (actionServiceObject?.status === REQUEST_ERROR) {
      notifications.show({
        title: t("bankSelection.anpassung.table.status"),
        message: t("bankSelection.anpassung.form.error"),
        color: "red",
      });
      setAdjustmentActionTargetId(undefined);
      setAdjustmentActionType(undefined);
    }
  }, [adjustmentActionTargetId, adjustmentActionType, bank, bankCreditRequestId, contractId, serviceObjects, t]);

  const openAnpassungModal = () => {
    setAnpassungCreditAmount(contractDetails?.creditAmount);
    setAnpassungPaymentPeriod(contractDetails?.paymentPeriod);
    setAnpassungMonthlyInstallment(contractDetails?.monthlyInstallment);
    setAnpassungCreditInsurance(contractDetails?.creditInsurance ? "YES" : "NO");
    setAnpassungComment("");
    setAnpassungOpen(true);
  };

  const handleAnpassungSubmit = (isCreating: boolean) => {
    if (!contractId || isCreating) return;

    const payload: ContractAdjustmentRequest = {
      contractId,
      creditAmount: anpassungCreditAmount,
      paymentPeriod: anpassungPaymentPeriod,
      monthlyInstallment: anpassungMonthlyInstallment,
      creditInsurance: anpassungCreditInsurance === "YES",
      comment: anpassungComment.trim(),
    };

    ContractAdjustmentService.createContractAdjustment(payload);
  };

  const handleAdjustmentStatusChange = (adjustmentId: number, value: string | null) => {
    if (!value) return;

    setAdjustmentActionTargetId(adjustmentId);

    if (value === "APPROVED") {
      setAdjustmentActionType("APPROVED");
      ContractAdjustmentService.approveContractAdjustment(adjustmentId);
    } else {
      setAdjustmentActionType("REJECTED");
      ContractAdjustmentService.cancelContractAdjustment(adjustmentId);
    }
  };

  const isAdjustmentActionPending = (adjustmentId: number): boolean => {
    if (adjustmentActionTargetId !== adjustmentId || !adjustmentActionType) return false;
    const requestKey =
      adjustmentActionType === "APPROVED"
        ? REQUEST_CONTRACT_ADJUSTMENT_APPROVE_ID + adjustmentId
        : REQUEST_CONTRACT_ADJUSTMENT_CANCEL_ID + adjustmentId;
    return serviceObjects[requestKey]?.status === REQUEST_PENDING;
  };

  return {
    contractAdjustments,
    anpassungOpen,
    anpassungCreditAmount,
    anpassungPaymentPeriod,
    anpassungMonthlyInstallment,
    anpassungCreditInsurance,
    anpassungComment,
    isCreatingContractAdjustment: contractAdjustmentCreateResponse?.status === REQUEST_PENDING,
    setAnpassungOpen,
    setAnpassungCreditAmount,
    setAnpassungPaymentPeriod,
    setAnpassungMonthlyInstallment,
    setAnpassungCreditInsurance,
    setAnpassungComment,
    openAnpassungModal,
    handleAnpassungSubmit,
    handleAdjustmentStatusChange,
    isAdjustmentActionPending,
  };
};
